import Phaser from "phaser";
import BackButton from "../GUI/BackButton";
import GameSettings from "../GameSettings";
import { MobileOverride } from "../Storage/SettingsStorage";

const W = 1200;
const H = 600;
const LABEL_X = 350;
const BTN_X = 750;

export default class SettingsScene extends Phaser.Scene {
  private mBackButton!: BackButton;
  private mDebugToggle!: Phaser.GameObjects.Text;
  private mModeButton!: Phaser.GameObjects.Text;
  private mStatusText!: Phaser.GameObjects.Text;
  private mStatusTimer?: Phaser.Time.TimerEvent;
  private mConfirmOverlay!: Phaser.GameObjects.Container;

  constructor() {
    super("SettingsScene");
  }

  preload() {
    BackButton.preload(this);
    this.load.image("mainBackground", "images/Scenes/MainMenu.png");
  }

  create() {
    const { width, height } = this.cameras.main;
    this.add.image(width / 2, height / 2, "mainBackground").setDisplaySize(width, height);

    this.add.text(W / 2, 60, "Settings", {
      fontSize: "52px",
      color: "#ffffff",
      fontStyle: "bold",
    }).setOrigin(0.5);

    this.buildDebugRow();
    this.buildModeRow();
    this.buildResetRow();

    this.mStatusText = this.add.text(W / 2, 550, "", {
      fontSize: "20px",
      color: "#88ff88",
    }).setOrigin(0.5);

    this.mConfirmOverlay = this.buildConfirmOverlay();
    this.mConfirmOverlay.setVisible(false);

    this.mBackButton = new BackButton(this, "MainMenu");
  }

  private buildDebugRow(): void {
    this.add.text(LABEL_X, 200, "Debug Mode", {
      fontSize: "28px",
      color: "#ffffff",
    }).setOrigin(0.5);

    this.mDebugToggle = this.makeToggleText(BTN_X, 200, GameSettings.debugMode);
    this.mDebugToggle.setInteractive({ useHandCursor: true });
    this.mDebugToggle.on("pointerdown", () => this.onDebugToggle());

    this.add
      .text(BTN_X, 234, "(reload required)", {
        fontSize: "14px",
        color: "#ffffff",
      })
      .setOrigin(0.5);
  }

  private buildModeRow(): void {
    this.add
      .text(LABEL_X, 320, "Input Mode", {
        fontSize: "28px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.mModeButton = this.makeModeText(BTN_X, 320, GameSettings.mobileOverride);
    this.mModeButton.setInteractive({ useHandCursor: true });
    this.mModeButton.on("pointerdown", () => this.onModeToggle());
  }

  private buildResetRow(): void {
    this.add
      .text(LABEL_X, 440, "Reset Progress", {
        fontSize: "28px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    const resetBtn = this.makeStyledText(BTN_X, 440, "  RESET  ", "#cc2222");
    resetBtn.setInteractive({ useHandCursor: true });
    resetBtn.on("pointerdown", () => this.mConfirmOverlay.setVisible(true));
  }

  private makeStyledText(x: number, y: number, label: string, bg: string): Phaser.GameObjects.Text {
    return this.add.text(x, y, label, {
      fontSize: "26px",
      color: "#ffffff",
      backgroundColor: bg,
      padding: { x: 18, y: 10 },
    }).setOrigin(0.5);
  }

  private makeToggleText(x: number, y: number, on: boolean): Phaser.GameObjects.Text {
    return this.makeStyledText(x, y, on ? "  ON  " : "  OFF  ", on ? "#22aa44" : "#aa2222");
  }

  private makeModeText(x: number, y: number, mode: MobileOverride): Phaser.GameObjects.Text {
    return this.makeStyledText(x, y, `  ${mode.toUpperCase()}  `, this.modeColor(mode));
  }

  private modeColor(mode: MobileOverride): string {
    const colors: Record<MobileOverride, string> = {
      auto: "#2255aa",
      mobile: "#aa8822",
      pc: "#226644",
    };
    return colors[mode];
  }

  private onDebugToggle(): void {
    const newVal = !GameSettings.debugMode;
    GameSettings.SetDebugMode(newVal);
    this.mDebugToggle.setText(newVal ? "  ON  " : "  OFF  ");
    this.mDebugToggle.setStyle({ backgroundColor: newVal ? "#22aa44" : "#aa2222" });
    this.showStatus("Reloading...");
    this.time.delayedCall(600, () => window.location.reload());
  }

  private onModeToggle(): void {
    const cycle: MobileOverride[] = ["auto", "mobile", "pc"];
    const next = cycle[(cycle.indexOf(GameSettings.mobileOverride) + 1) % cycle.length];
    GameSettings.SetMobileOverride(next);
    this.mModeButton.setText(`  ${next.toUpperCase()}  `);
    this.mModeButton.setStyle({ backgroundColor: this.modeColor(next) });
  }

  private onConfirmReset(): void {
    GameSettings.ResetProgress();
    this.mConfirmOverlay.setVisible(false);
    this.showStatus("Progress reset!");
  }

  private buildConfirmOverlay(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0).setDepth(10);

    const shade = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75);
    container.add(shade);

    const box = this.add.rectangle(W / 2, H / 2, 520, 220, 0x222233);
    container.add(box);

    const msg = this.add.text(W / 2, H / 2 - 50, "Reset all progress?", {
      fontSize: "28px",
      color: "#ffffff",
    }).setOrigin(0.5);
    container.add(msg);

    const yesBtn = this.add.text(W / 2 - 90, H / 2 + 45, "  YES  ", {
      fontSize: "26px",
      color: "#ffffff",
      backgroundColor: "#cc2222",
      padding: { x: 16, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    yesBtn.on("pointerdown", () => this.onConfirmReset());
    container.add(yesBtn);

    const noBtn = this.add.text(W / 2 + 90, H / 2 + 45, "  NO  ", {
      fontSize: "26px",
      color: "#ffffff",
      backgroundColor: "#334466",
      padding: { x: 16, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    noBtn.on("pointerdown", () => this.mConfirmOverlay.setVisible(false));
    container.add(noBtn);

    return container;
  }

  private showStatus(msg: string): void {
    this.mStatusText.setText(msg);
    if (this.mStatusTimer) {
      this.mStatusTimer.remove();
    }
    this.mStatusTimer = this.time.delayedCall(3000, () => this.mStatusText.setText(""));
  }
}
